/**
 * Confirmation for actions that cannot be undone.
 *
 * Two levels, deliberately. Most destructive actions need one clear
 * confirmation. The genuinely irreversible ones — deleting a signed document,
 * publishing a template — take a typed word, because a receptionist under time
 * pressure with a guest waiting will click through anything that only needs
 * one click.
 *
 * Reversible actions get no dialog at all (docs/08-uiux.md §1, principle 1).
 */

import { useEffect, useRef, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button, Input } from '@/components/ui/primitives'

export function ConfirmDestructive({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Keep it',
  typeToConfirm,
  onConfirm,
  onCancel,
  loading,
}: {
  open: boolean
  title: string
  description: string
  confirmLabel?: string
  /** Say what is being kept. The default reads wrong for a batch. */
  cancelLabel?: string
  /** When set, the user must type this word exactly. */
  typeToConfirm?: string
  onConfirm: () => void
  onCancel: () => void
  loading?: boolean
}) {
  const [typed, setTyped] = useState('')
  const dialogRef = useRef<HTMLDivElement>(null)
  const confirmRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) {
      setTyped('')
      return
    }
    // Esc always closes an overlay; nothing here is a keyboard trap.
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', onKey)
    confirmRef.current?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  if (!open) return null

  const ready = !typeToConfirm || typed.trim().toLowerCase() === typeToConfirm.toLowerCase()

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center scrim px-4"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel()
      }}
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-description"
        className="fade-in w-full max-w-[26rem] rounded-[18px] border border-[var(--border)] bg-[var(--surface)] p-6 shadow-[var(--shadow-lg)]"
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 rounded-full bg-[var(--danger-subtle)] p-2 text-[var(--danger)]">
            <AlertTriangle className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 id="confirm-title" className="text-[15px] font-semibold">
              {title}
            </h2>
            <p id="confirm-description" className="mt-1 text-[13px] text-[var(--text-muted)]">
              {description}
            </p>
          </div>
        </div>

        {typeToConfirm && (
          <div className="mt-4">
            <label htmlFor="confirm-input" className="text-[12px] text-[var(--text-muted)]">
              Type <span className="font-mono font-semibold">{typeToConfirm}</span> to confirm
            </label>
            <Input
              id="confirm-input"
              className="mt-1"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              autoFocus
            />
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            variant="danger"
            disabled={!ready}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
